import {
  useDeleteBeneficiary,
  useGetBeneficiaryList
} from 'common/queries-and-mutations/beneficiary';
import Badge from 'components/Badge';
import { Icon, Icons } from 'components/Icon';
import { Loader } from 'components/Loader';
import TableActionWidget from 'components/TableWidget/TableActionWidget';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import EditBeneficiaryModal from '../Beneficiary/EditBeneficiaryModal';
import Button from 'components/Form/Button';

export default function BeneficiaryTable({
  hideTable = false
}: {
  hideTable?: boolean;
}) {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<null | number>(null);
  const [userId, setUserId] = useState<null | number>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const { data, isLoading, refetch } = useGetBeneficiaryList();

  const {
    mutate: deleteBeneficiary,
    isLoading: isDeleting,
    isSuccess: isDeleted
  } = useDeleteBeneficiary();

  useEffect(() => {
    if (isDeleted) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleting, isDeleted]);

  const shouldHide = !(!data?.length && hideTable);

  return (
    <div>
      <div className="relative min-h-[21.8rem] overflow-x-auto bg-white sm:rounded-lg">
        <div className="mb-3 flex items-center justify-between">
          <div className="p-5 text-left text-lg font-semibold text-gray-900">
            Beneficiary List
          </div>
          <Link to="/dashboard/beneficiary">
            <Button
              variant="secondary"
              size="md"
              className="mr-8 mt-1 w-[7rem] rounded border-none bg-[#F0F2FA]"
              label="View All"
            ></Button>
          </Link>
        </div>
        <div className="flex flex-col items-center justify-center">
          {isLoading ? (
            <div className="mt-20 flex flex-col items-center justify-center">
              <Loader color={'#FF6606'} height={50} width={50} />
            </div>
          ) : (
            <>
              {shouldHide ? (
                <table className="my-[1.4rem] w-full text-left text-sm">
                  <thead className="border-b bg-white text-xs text-[#1F4173]">
                    <tr>
                      <th scope="col" className="px-6 py-3">
                        Name
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Email
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Relationship
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="px-6 py-4">
                    {!data || data.length < 1 ? (
                      <tr className="text-center">
                        <td colSpan={5} className="py-10 text-lg">
                          No results found
                        </td>
                      </tr>
                    ) : (
                      data?.slice(0, 5).map(beneficiary => (
                        <tr
                          key={beneficiary.id}
                          className="border-b bg-white hover:bg-gray-50"
                        >
                          <td className="px-6 py-4 text-xs font-normal  text-[#353535]">
                            {`${beneficiary.beneficiaryFirstName} ${beneficiary.beneficiaryMiddleName} ${beneficiary.beneficiaryLastName}`}
                          </td>
                          <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                            {beneficiary.emailAddress}
                          </td>
                          <td className="px-6 py-4 text-xs font-normal text-[#353535]">
                            {beneficiary.beneficiaryType}
                          </td>
                          <td className="px-6 py-4">
                            <Badge
                              title={
                                beneficiary.isAssigned
                                  ? 'Assigned'
                                  : 'Unassigned'
                              }
                              type={
                                beneficiary.isAssigned ? 'success' : 'danger'
                              }
                              size="sm"
                            />
                          </td>
                          <td className="column-to-hide px-6 py-4">
                            <button
                              type="button"
                              role="button"
                              tabIndex={-1}
                              onKeyDown={() => {
                                setSelectedId(beneficiary.id);
                              }}
                              onClick={() => {
                                setSelectedId(beneficiary.id);
                              }}
                            >
                              <Icon name={Icons.MoreMenu} />
                            </button>
                            {selectedId === beneficiary.id && isDeleting && (
                              <Loader height={20} width={20} type={'Wait'} />
                            )}
                            {selectedId === beneficiary.id && (
                              <TableActionWidget
                                onViewClick={() =>
                                  navigate(
                                    `beneficiary/profile/${beneficiary.id}`
                                  )
                                }
                                onEditClick={() => {
                                  setShowEditModal(true);
                                  setUserId(beneficiary.id);
                                }}
                                onRemoveClick={() => {
                                  if (beneficiary.id) {
                                    deleteBeneficiary(beneficiary.id);
                                  }
                                }}
                                onDismiss={() => setSelectedId(null)}
                              />
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : (
                <>
                  <div className="mt-10 flex flex-col items-center justify-center">
                    No Beneficiary Added
                  </div>
                  <Button
                    variant="outline"
                    size="custom"
                    className="mt-2 rounded-sm border-[#FF7A00] bg-[#FF6600] px-8 py-2.5 text-center text-xs font-medium text-white"
                    label="Add Beneficiaries"
                    type="button"
                  ></Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {showEditModal && userId && (
        <EditBeneficiaryModal
          onClose={() => {
            setShowEditModal(false);
            refetch();
          }}
          onSuccess={() => {
            refetch();
            setShowEditModal(false);
            setUserId(null);
          }}
          beneficiaryId={userId}
        />
      )}
    </div>
  );
}
